import type { Options } from '@angular-slider/ngx-slider';
import type { OnDestroy, OnInit, ElementRef } from '@angular/core';
import type { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import type { Bounds } from '@placemarkio/geo-viewport';
import type { MapWidgetData } from '@web/widgets/map/type';
import type { MapItem } from '@web/widgets/map/type/map-widget.type';
import type { Observable } from 'rxjs';

import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { viewport } from '@placemarkio/geo-viewport';
import { map } from 'rxjs';

import { CredentialsFacade } from '@web/store/credentials/credentials.facade';
import { type WebLegacyWidgetEntity } from '@web/types';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { MapSettingsComponent } from '@web/widgets/map/components/map-control/map-settings/map-settings.component';
import { MapStyle } from '@web/widgets/map/type/map-widget.type';

@Component({
    selector: 'web-map-control',
    templateUrl: './map-control.component.html',
    styleUrls: ['./map-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, A11yModule, LetDirective, MapSettingsComponent],
})
export class MapControlComponent extends WebBaseControlComponent<MapWidgetData> implements OnInit, OnDestroy {
    @ViewChild('apiKeyInput', { static: false }) apiKeyInput: ElementRef<HTMLInputElement>;

    private credentialsFacade: CredentialsFacade = inject(CredentialsFacade);

    public readonly minMapZoom: number = 1;
    public readonly maxMapZoom: number = 22;

    public disabledLastDeleteBtn: boolean;
    public apiKeyEditing: boolean = false;

    public mapStyleList = Object.values(MapStyle);

    public editableMapItem: MapItem = null;
    public editableMapItemIndex: number = null;

    public mapsApiKey$: Observable<string> = this.credentialsFacade.mapsApiKey$;
    public isUser$ = this.appFacade.isAdmin$.pipe(map((v) => !v));

    public sliderOptions: Options = {
        minLimit: this.minMapZoom,
        floor: this.minMapZoom,
        ceil: this.maxMapZoom,
        step: 1,
    };

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group({
                apiKey: null,
                addresses: this.fb.array([]),
                zoom: null,
                center: null,
                mapStyle: null,
            }),
        });
    }

    protected setFormData(): void {
        this.addresses.clear({ emitEvent: false });
        this.widgetData.addresses.forEach((mapItem: MapItem) =>
            this.addresses.push(this.fb.control(mapItem), { emitEvent: false })
        );
        this.controlForm.patchValue(
            {
                data: { ...this.widgetData },
                styles: { ...this.widgetStyles },
            },
            { emitEvent: false }
        );

        if (this.addresses.length === 1) {
            this.disabledLastDeleteBtn = true;
        } else {
            this.disabledLastDeleteBtn = false;
        }
    }

    public setApiKey(apiKey: string): void {
        this.credentialsFacade.setMapsApiKey(apiKey);
        this.apiKeyEditing = false;
        this.apiKeyInput.nativeElement.blur();
    }

    public editApiKey(): void {
        this.apiKeyEditing = true;
    }

    public get data(): UntypedFormControl {
        return this.controlForm.get('data') as UntypedFormControl;
    }

    public get addresses(): UntypedFormArray {
        return this.data.get('addresses') as UntypedFormArray;
    }

    public get mapStyle(): UntypedFormControl {
        return this.data.get('mapStyle') as UntypedFormControl;
    }

    public get zoom(): UntypedFormControl {
        return this.data.get('zoom') as UntypedFormControl;
    }

    public setZoom(value: number): void {
        this.zoom.patchValue(value);
    }

    public setZoomSlider(slide): void {
        this.zoom.patchValue(slide.value);
    }

    public setMapStyle(styleItem: MapStyle): void {
        this.mapStyle.patchValue(styleItem);
    }

    public getWidgetId(widget: WebLegacyWidgetEntity) {
        return widget.displayId ? widget.displayId : `widget-${widget.id}`;
    }

    public get mapElement(): HTMLDivElement {
        return document.querySelector(`[id='${this.getWidgetId(this.widget)}'] .map-container`);
    }

    public addMapItem(): void {
        this.editMapItem({} as MapItem, null);
    }

    public editMapItem(mapItem: MapItem, index) {
        // this.updateWidgetData({ tempCenter: mapItem?.position || null });
        if (mapItem?.position) {
            // !currently useless because of blur on the map while editing
            // const bounds = new google.maps.LatLngBounds(mapItem.position);
            // const elem: HTMLDivElement = this.mapElement;
            // const viewportLiteral = viewport(Object.values(bounds.toJSON()) as Bounds, [elem.clientHeight, elem.clientWidth], {
            //     maxzoom: 15,
            // });
            // this.updateWidgetData({
            // tempCenter: {
            //     lat: viewportLiteral.center[0],
            //     lng: viewportLiteral.center[1],
            // },
            // zoom: viewportLiteral.zoom,
            // });
        } else {
            this.reCalcBounds();
        }
        this.editableMapItem = mapItem;
        this.editableMapItemIndex = index;
    }

    public removeMapItem(i) {
        if (this.addresses.value.length === 1) {
            return;
        }

        this.addresses.removeAt(i);

        this.reCalcBounds();
    }

    public saveMapItem(data: MapItem): void {
        if (this.editableMapItemIndex === null) {
            this.addresses.push(this.fb.control(data));
        } else {
            this.addresses.at(this.editableMapItemIndex).patchValue(data);
        }

        this.reCalcBounds();
        this.editMapItem(null, null);
    }

    public reCalcBounds(): void {
        const bounds = new google.maps.LatLngBounds();

        this.addresses.value.forEach((item: MapItem) => {
            bounds.extend(item.position);
        });

        const elem: HTMLDivElement = this.mapElement;

        const viewportLiteral = viewport(
            Object.values(bounds.toJSON()) as Bounds,
            [elem.clientHeight, elem.clientWidth],
            {
                maxzoom: 15,
                minzoom: 1,
                tileSize: 512,
            }
        );

        this.updateWidgetData({
            tempCenter: null,
            center: {
                lat: viewportLiteral.center[0],
                lng: viewportLiteral.center[1],
            },
            zoom: viewportLiteral.zoom,
        });
    }

    public setNewDisplayId(newId: string): void {
        this.updateWidgetDisplayId(newId);
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if ('google' in window && this.mapElement && this.editableMapItem) {
            this.reCalcBounds();
        }
    }
}
